import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import OfflineNotice from '@/components/Common/OfflineNotice';
import { isChunkLoadError, recoverFromChunkError } from '@/services/shellRecovery';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo);
    // TODO: Send to error reporting service (Sentry, etc.)

    // A page whose CODE would not load is not a bug in that page, and the two
    // reasons it happens need opposite responses. Online, the running build is
    // stale — hashed assets do not survive a deploy, and the static host
    // answers the missing one with index.html and a 200 — so one reload picks
    // up the current build. Offline, the chunk is simply not here and a reload
    // would only throw away a working page; render() shows OfflineNotice for
    // that case instead. See services/shellRecovery.ts.
    recoverFromChunkError(error, 'boundary');
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // "Something went wrong … contact support if the problem persists" is
      // the wrong sentence for a wifi outage, and the raw
      // "Failed to fetch dynamically imported module: https://…" underneath it
      // is not something anybody can act on.
      if (isChunkLoadError(this.state.error)) {
        return <OfflineNotice onRetry={this.handleReset} />;
      }

      return (
        <div className="flex min-h-[40vh] flex-col items-center justify-center px-4 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-danger-50">
            <ExclamationTriangleIcon className="h-7 w-7 text-status-red" />
          </div>
          <h2 className="text-lg font-semibold text-content">Something went wrong</h2>
          <p className="mt-1.5 max-w-md text-sm text-content-muted">
            An unexpected error occurred. Please try again or contact support if the problem
            persists.
          </p>
          {this.state.error && (
            <pre className="mt-4 max-w-lg overflow-x-auto rounded-lg bg-surface-muted p-3 text-left text-xs text-content-tertiary">
              {this.state.error.message}
            </pre>
          )}
          <button onClick={this.handleReset} className="btn-primary mt-6">
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
